import { useQuery } from "@apollo/client";
import { GarageSlotsQuery, GET_GARAGE_SLOTS } from "@App/graphql/queries";
import { useQueryPollingWhileWindowFocused } from "@App/hooks/api/useQueryPollingWhileWindowFocused";
import { useCalculation } from "@App/hooks/useCalculation";
import { endOfDay, startOfDay } from "date-fns";
import { useSelector } from "react-redux";
import { State } from "@App/state";
import { useContext } from "@App/hooks/useContext";
import { formatDate, toIsoString } from "@App/utils/dates";

export const useSlots = (date?: Date) => {
  const slot = useSelector((state: State) => state.orders.slot);

  const {
    organization,
    service,
    garage,
    filterBrand,
    brand,
    generation,
    motorization,
    body,
    gearbox,
    mileage,
    initialRegistrationDate,
    subRepairs,
    equipments,
    premiumServices,
  } = useCalculation();

  const { getContext } = useContext();

  const { loading, data, error, refetch, startPolling, stopPolling } = useQuery<GarageSlotsQuery>(
    GET_GARAGE_SLOTS,
    {
      variables: {
        organization: organization ? organization.id : null,
        garage: garage ? garage.id : null,
        services: service ? [service.id] : null,
        from: date ? toIsoString(startOfDay(date)) : null,
        to: date ? toIsoString(endOfDay(date)) : null,
        brand: brand ? brand.id : filterBrand ? filterBrand.id : null,
        generation: generation ? generation.id : null,
        motorization: motorization ? motorization.id : null,
        body: body ? body.id : null,
        gearbox: gearbox ? gearbox.id : null,
        mileage: mileage,
        registrationDate: initialRegistrationDate
          ? formatDate(initialRegistrationDate, "yyyy-MM-dd")
          : null,
        premiumServices:
          premiumServices && premiumServices.length > 0 ? premiumServices.map((p) => p.id) : null,
        equipments: equipments.map((equipment) => equipment.name),
        subRepairs: subRepairs,
        session_id: slot && slot.session_id ? slot.session_id : null,
      },
      skip: !organization || !garage || !date || isNaN(date?.getTime()),
      context: getContext(garage),
    },
  );

  // only poll for new days if window is focused
  useQueryPollingWhileWindowFocused({
    pollInterval: 300000,
    loading,
    skip: !organization || !garage || !date,
    refetch: refetch,
    startPolling: startPolling,
    stopPolling: stopPolling,
  });

  return {
    loading: loading,
    error: error,
    slots: data ? data.garage.slots : [],
    nextSlot: data && data.garage.earliestSlot ? data.garage.earliestSlot : null,
  };
};
